import React, { FC, PropsWithChildren, useEffect, useState } from "react";
import AuthenticationContext, {
  AuthenticationContextValue,
} from "../../context/authenticationContext";
import { Accounts, WebmapSelf, Webmaps } from "../../api/portal";
import { useSnackbar } from "notistack";
import { AccountSelf } from "../../types/account";
import { getWebmapDetails } from "../../api/projects";
import FullScreenLogoWithText from "../DurationIconWithText";

/**
 * Wrapper for the authentication context. Passes user info as well as
 * authentication loading states and the state setter into the context.
 */
const AuthenticationProvider: FC<PropsWithChildren> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [authenticationContextValue, setAuthenticationContextValue] =
    useState<AuthenticationContextValue>({
      isLoadingMainAccountData: true,
      isLoadingLogin: false,
      hasInitialUserLoadFailed: null,
      isLoadingAdditionalAccountData: false,
      isAuthCompleted: false,
      shouldInitializeUserLoad: true,
      authFailMessage: "",
    });

  useEffect(() => {
    if (authenticationContextValue.shouldInitializeUserLoad) {
      Promise.all([Accounts.getMe(), Webmaps.getMe()])
        .then(([user, webmaps]: [AccountSelf, WebmapSelf[]]) => {
          setAuthenticationContextValue((curr: AuthenticationContextValue) => {
            return {
              ...curr,
              isLoadingMainAccountData: false,
              hasInitialUserLoadFailed: false,
              isLoadingAdditionalAccountData: true,
              isAuthCompleted: true,
              user,
            };
          });
          // getWebmapDetails
          getWebmapDetails(webmaps, user).then((enrichedWebmaps) => {
            setAuthenticationContextValue(
              (curr: AuthenticationContextValue) => {
                return {
                  ...curr,
                  isLoadingAdditionalAccountData: false,
                  webmaps: enrichedWebmaps,
                };
              }
            );
          });
        })
        .catch(() => {
          setAuthenticationContextValue((curr) => {
            // we tried the account/webmap request to the Portal, but the cookie wasn't there
            return {
              ...curr,
              isLoadingLogin: false,
              isLoadingMainAccountData: false,
              hasInitialUserLoadFailed: true,
              isAuthCompleted: false,
            };
          });
        })
        .finally(() => {
          setAuthenticationContextValue((curr) => {
            return {
              ...curr,
              shouldInitializeUserLoad: false,
            };
          });
        });
    }
  }, [authenticationContextValue.shouldInitializeUserLoad, enqueueSnackbar]);

  if (!authenticationContextValue.user) {
    if (
      authenticationContextValue.isLoadingMainAccountData ||
      authenticationContextValue.isLoadingAdditionalAccountData
    ) {
      return (
        <div style={{ height: "100vh", width: "100vw" }}>
          <FullScreenLogoWithText>Lade...</FullScreenLogoWithText>
        </div>
      );
    }
  }

  return (
    <AuthenticationContext.Provider
      value={{ ...authenticationContextValue, setAuthenticationContextValue }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationProvider;
